import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, List, ListItem, ListIcon, Button } from "@chakra-ui/react";
import { FaHome, FaDollarSign, FaChartLine, FaCheckCircle, FaUserFriends } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const GOLD = "#C69214";
const AMBER = "#F2B705";

const SolarServicesInvestmentProperty: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/services/investment-property"
        fallbackTitle="Investment Property Solar"
        fallbackDescription="Increase property value and rental appeal with solar energy systems"
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaHome} boxSize={16} color={AMBER} />
            <Heading size="2xl" fontWeight="bold">
              Solar for Investment Properties
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Boost rental income and property value with solar
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* ROI Benefits */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Landlord Benefits</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="2xl">
            Solar systems deliver multiple revenue streams for property investors
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          <Box bg="white" p={8} borderRadius="xl" border="2px solid" borderColor={GOLD}>
            <Icon as={FaDollarSign} boxSize={12} color={GOLD} mb={4} />
            <Heading size="md" mb={3}>Higher Rent</Heading>
            <Text color="gray.600" fontSize="sm" mb={4}>
              Properties with solar command 5–10% higher rental rates
            </Text>
            <Text fontWeight="bold" color={GOLD}>+$20–40/week potential</Text>
          </Box>

          <Box bg="white" p={8} borderRadius="xl" border="2px solid" borderColor={GOLD}>
            <Icon as={FaChartLine} boxSize={12} color={GOLD} mb={4} />
            <Heading size="md" mb={3}>Property Value</Heading>
            <Text color="gray.600" fontSize="sm" mb={4}>
              Solar adds tangible value to property assessments
            </Text>
            <Text fontWeight="bold" color={GOLD}>3–5% value increase</Text>
          </Box>

          <Box bg="white" p={8} borderRadius="xl" border="2px solid" borderColor={AMBER}>
            <Icon as={FaUserFriends} boxSize={12} color={AMBER} mb={4} />
            <Heading size="md" mb={3}>Tenant Retention</Heading>
            <Text color="gray.600" fontSize="sm" mb={4}>
              Lower power bills mean happier, longer-staying tenants
            </Text>
            <Text fontWeight="bold" color={AMBER}>Reduced vacancy rates</Text>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Rental Appeal */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
            <VStack align="flex-start" spacing={6}>
              <Heading size="xl">Stand Out in the Market</Heading>
              <Text fontSize="lg" color="gray.600">
                Solar-equipped properties attract quality tenants and reduce time on market
              </Text>
              <List spacing={4}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={GOLD} boxSize={6} />
                  <Text as="span" fontWeight="medium">Lower Utility Costs</Text> – Major selling point for tenants
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={GOLD} boxSize={6} />
                  <Text as="span" fontWeight="medium">Eco-Conscious Appeal</Text> – Attracts environmentally aware renters
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={GOLD} boxSize={6} />
                  <Text as="span" fontWeight="medium">Modern Amenity</Text> – Shows the property is well-maintained
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={GOLD} boxSize={6} />
                  <Text as="span" fontWeight="medium">Faster Leasing</Text> – Solar properties attract tenants more quickly in competitive rental markets
                </ListItem>
              </List>
            </VStack>

            <Box
              bg="white"
              p={8}
              borderRadius="xl"
              shadow="xl"
              border="2px solid"
              borderColor={GOLD}
            >
              <VStack spacing={6} align="stretch">
                <Heading size="md" color={NAVY}>Sample ROI Calculation</Heading>
                <Box>
                  <Text fontSize="sm" color="gray.500">System Cost (6.6kW)</Text>
                  <Text fontSize="2xl" fontWeight="bold">$5,500</Text>
                  <Text fontSize="xs" color="gray.400">After rebates</Text>
                </Box>
                <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
                  <Text fontSize="sm" color="gray.500">Annual Benefits</Text>
                  <VStack align="flex-start" spacing={2} mt={2}>
                    <Text fontSize="sm">Rental premium: <Text as="span" fontWeight="bold">+$1,560/yr</Text></Text>
                    <Text fontSize="sm">Tax deductions: <Text as="span" fontWeight="bold">$550/yr</Text></Text>
                  </VStack>
                </Box>
                <Box bg="rgba(198, 146, 20, 0.08)" p={4} borderRadius="lg" border="1px solid" borderColor={GOLD}>
                  <Text fontSize="sm" color="gray.600" mb={1}>Payback Period</Text>
                  <Text fontSize="3xl" fontWeight="bold" color={GOLD}>2.6 years</Text>
                </Box>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Tax Benefits */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Tax Advantages for Investors</Heading>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <Box bg="gray.50" p={8} borderRadius="lg" border="1px solid" borderColor="gray.200">
            <Heading size="md" mb={4} color={NAVY}>Instant Asset Write-Off</Heading>
            <Text color="gray.600" mb={4}>
              Deduct the full cost of your solar system in the year of installation
            </Text>
            <List spacing={2}>
              <ListItem fontSize="sm">
                <ListIcon as={FaCheckCircle} color={GOLD} />
                Available for businesses and investment properties
              </ListItem>
              <ListItem fontSize="sm">
                <ListIcon as={FaCheckCircle} color={GOLD} />
                Reduces taxable income in installation year
              </ListItem>
            </List>
          </Box>

          <Box bg="gray.50" p={8} borderRadius="lg" border="1px solid" borderColor="gray.200">
            <Heading size="md" mb={4} color={NAVY}>Depreciation Benefits</Heading>
            <Text color="gray.600" mb={4}>
              Claim depreciation on solar equipment over its effective life
            </Text>
            <List spacing={2}>
              <ListItem fontSize="sm">
                <ListIcon as={FaCheckCircle} color={GOLD} />
                Panels depreciated over 20–30 years
              </ListItem>
              <ListItem fontSize="sm">
                <ListIcon as={FaCheckCircle} color={GOLD} />
                Additional annual tax deductions
              </ListItem>
            </List>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center" color="white">
            <Heading size="xl">Maximise Your Investment Property Returns</Heading>
            <Text fontSize="lg" opacity={0.9} maxW="2xl">
              Schedule a consultation to discuss solar for your rental properties
            </Text>
            <Button size="lg" bg={GOLD} color="white" _hover={{ bg: "#a07e10" }}>
              Get Investor Quote
            </Button>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarServicesInvestmentProperty;
